import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  inject,
  Injector,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import { map, Observable, of, takeUntil } from 'rxjs';
import { TuiPreviewDialogService } from '@taiga-ui/kit';
import { PolymorpheusComponent } from '@taiga-ui/polymorpheus';
import { BaseComponent } from '@src/app/modules/base';
import { Multimedia, ViewMode } from '@src/models';
import { GetImageType } from '@src/models/images';

import { PhotoSize } from '../base-photo/types';
import { PhotoPreviewComponent } from '../base-photo/photo-preview/photo-preview.component';

@Component({
  template: '',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export abstract class BasePhotoWrapperComponent extends BaseComponent implements OnChanges {
  private readonly previewDialogService = inject(TuiPreviewDialogService);
  private readonly injector = inject(Injector);

  @Input() mode: ViewMode = 'view';
  @Input() text?: string;
  @Input() newPhoto?: Multimedia;
  @Input() id?: string;
  @Input() photoSrc?: string;
  @Input() showPreview: boolean = true;
  @Output() photoChange = new EventEmitter<Multimedia | undefined>();
  @Output() photoDeleted = new EventEmitter<void>();

  @Input() size: PhotoSize = 'xxl';
  @Input() width?: string;
  @Input() height?: string;
  @Input() borderRadius?: string;
  @Input() rounded: boolean = false;
  @Input() loading: boolean = false;

  protected photoUrl?: string;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.id || changes.photoSrc) {
      this.photoUrl = undefined;
      this.setPhotoUrl();
    }
  }

  abstract apiDelete(id: string): Observable<any>;
  abstract getStorePhotoSrc(id: string, size: GetImageType): string | undefined;
  abstract getPhotoBlob(id: string, size: GetImageType): Observable<Blob | null>;

  protected openPreviewDialog() {
    if (!this.id && !this.photoSrc) return;

    this.previewDialogService
      .open(new PolymorpheusComponent(PhotoPreviewComponent, this.injector), {
        data: {
          title: this.text,
          photoSrc$: this.getPhotoUrl(this.id, 'fullSize'),
        },
      })
      .subscribe();
  }

  protected onPhotoDelete() {
    if (!this.id) return;
    this.loading = true;

    this.apiDelete(this.id)
      .pipe(takeUntil(this.destroyed$$))
      .subscribe(() => {
        this.photoUrl = undefined;
        this.loading = false;
        this.photoDeleted.emit();
        this.cdr.markForCheck();
      });
  }

  private setPhotoUrl(photoSize: GetImageType = 'thumbnail') {
    this.getPhotoUrl(this.id, photoSize).subscribe(photoUrl => {
      this.photoUrl = photoUrl;
      this.cdr.markForCheck();
    });
  }

  private getPhotoUrl(id?: string, photoSize: GetImageType = 'thumbnail') {
    // TODO: Вынести логику для внешних ссылок в отдельный модуль
    if (this.photoSrc) {
      return of(this.photoSrc);
    }

    if (id) {
      const storePhotoSrc = this.getStorePhotoSrc(id, photoSize);

      if (!storePhotoSrc) {
        return this.getPhotoBlob(id, photoSize).pipe(
          takeUntil(this.destroyed$$),
          map(blob => (blob ? window.URL.createObjectURL(blob) : undefined)),
        );
      }

      return of(storePhotoSrc);
    }

    return of();
  }
}
